import React, { useState, useMemo } from "react";
import { Table, Form, Pagination } from "react-bootstrap";
import { ChevronUp, ChevronDown, ChevronRight, ChevronDown as Expand } from "lucide-react";

const TokenUsageTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });
  const [dateRange, setDateRange] = useState({ fromDate: "", toDate: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const rowsPerPageOptions = [10, 25, 50, 100];
  const sortableColumns = ["date", "filename", "total_tokens", "remaining_allowance"];

  const getTokenUsageData = () => {
    if (!data?.token_usage || !Array.isArray(data.token_usage)) {
      return [];
    }
    
    return data.token_usage.map(entry => {
      // Create a new object to avoid mutating the original data
      const processedEntry = { ...entry };
      
      // Create a sorted copy of iterations if they exist
      if (processedEntry.iterations && processedEntry.iterations.length > 0) {
        processedEntry.iterations = [...processedEntry.iterations].sort((a, b) => b.iteration - a.iteration);
      }
      
      return {
        ...processedEntry,
        // Use the first iteration or an empty object if no iterations
        ...((processedEntry.iterations && processedEntry.iterations[0]) || {}),
        rowId: `${processedEntry.date}-${processedEntry.filename}`,
      };
    });
  };

  const toggleRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSort = (key) => {
    if (!sortableColumns.includes(key)) return;
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredAndSortedData = useMemo(() => {
    return getTokenUsageData()
      .filter((row) => {
        if (!dateRange.fromDate && !dateRange.toDate) return true;
        
        const rowDate = new Date(row.date);
        const fromDateObj = dateRange.fromDate ? new Date(dateRange.fromDate) : null;
        const toDateObj = dateRange.toDate ? new Date(dateRange.toDate) : null;

        if (fromDateObj && toDateObj) {
          return rowDate >= fromDateObj && rowDate <= toDateObj;
        } else if (fromDateObj) {
          return rowDate >= fromDateObj;
        } else if (toDateObj) {
          return rowDate <= toDateObj;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortConfig.key === "date") {
          return sortConfig.direction === "asc"
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else if (sortConfig.key === "filename") {
          return sortConfig.direction === "asc"
            ? a.filename.localeCompare(b.filename)
            : b.filename.localeCompare(a.filename);
        }
        return sortConfig.direction === "asc"
          ? a[sortConfig.key] - b[sortConfig.key]
          : b[sortConfig.key] - a[sortConfig.key];
      });
  }, [data, dateRange, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const SortIndicator = ({ columnKey }) => {
    if (!sortableColumns.includes(columnKey)) return null;
    return (
      <div className="ms-1">
        {sortConfig.key === columnKey && sortConfig.direction === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
    );
  };

  const columnConfig = [
    { key: "expand", label: "" },
    { key: "date", label: "Date" },
    { key: "filename", label: "Filename" },
    { key: "iteration", label: "Iteration" },
    { key: "execution_time", label: "Execution Time (s)" },
    { key: "input_tokens", label: "Input Tokens" },
    { key: "output_tokens", label: "Output Tokens" },
    { key: "total_tokens", label: "Total Tokens" },
    { key: "input_token_cost", label: "Input Cost" },
    { key: "output_token_cost", label: "Output Cost" },
    { key: "remaining_allowance", label: "Remaining Allowance" }
  ];

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
    }

    return items;
  };

  if (!data || !data.token_usage || data.token_usage.length === 0) {
    return (
      <div className="text-center p-4">
        <p>No token usage data available.</p>
      </div>
    );
  }

  return (
    <div className="token-data-detail">
      <div className="top-row d-flex justify-content-between p-3">
        <div className="view-page">
          <Form.Select value={rowsPerPage} onChange={handleRowsPerPageChange} style={{ width: "auto" }} size="sm">
            {rowsPerPageOptions.map(option => <option key={option} value={option}>{option}</option>)}
          </Form.Select>
          <span>entries per page</span>
        </div>
        <div className="search-form">
          <Form.Group>
            <Form.Label>From Date</Form.Label>
            <Form.Control type="date" name="fromDate" value={dateRange.fromDate} onChange={handleDateChange} max={dateRange.toDate || undefined} />
          </Form.Group>
          <Form.Group>
            <Form.Label>To Date</Form.Label>
            <Form.Control type="date" name="toDate" value={dateRange.toDate} onChange={handleDateChange} min={dateRange.fromDate || undefined} />
          </Form.Group>
          <button className="btn btn-primary" onClick={() => { setDateRange({ fromDate: "", toDate: "" }); setCurrentPage(1); }}>
            Clear
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover className="token-detail-table">
          <thead>
            <tr>
              {columnConfig.map(({ key, label }) => (
                <th 
                  key={key} 
                  onClick={() => handleSort(key)} 
                  style={{ cursor: sortableColumns.includes(key) ? "pointer" : "default" }}
                >
                  <div className="d-flex align-items-center">
                    {label}
                    <SortIndicator columnKey={key} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((row) => (
              <React.Fragment key={row.rowId}>
                <tr>
                  <td>
                    {row.iterations && row.iterations.length > 1 && 
                      <button 
                        className="btn btn-link p-0"
                        onClick={() => toggleRowExpansion(row.rowId)}
                      >
                        {expandedRows.has(row.rowId) ? <Expand size={16} /> : <ChevronRight size={16} />}
                      </button>
                    }
                  </td>
                  <td className="text-center">{row.date}</td>
                  <td>{row.filename}</td>
                  <td className="text-end">{row.iteration?.toLocaleString()}</td>
                  <td className="text-end">{row.execution_time?.toLocaleString()}</td>
                  <td className="text-end">{row.input_tokens?.toLocaleString()}</td>
                  <td className="text-end">{row.output_tokens?.toLocaleString()}</td>
                  <td className="text-end">{row.total_tokens?.toLocaleString()}</td>
                  <td className="text-end">${row.input_token_cost?.toFixed(4)}</td>
                  <td className="text-end">${row.output_token_cost?.toFixed(4)}</td>
                  <td className="text-end">{row.remaining_allowance?.toLocaleString()}</td>
                </tr>
                {row.iterations && row.iterations.length > 1 && expandedRows.has(row.rowId) && 
                  row.iterations.slice(1).map((iteration) => (
                    <tr key={`${row.rowId}-iteration-${iteration.iteration}`} className="bg-light">
                      <td colSpan={3}></td>
                      <td className="text-end">{iteration.iteration}</td>
                      <td className="text-end">{iteration.execution_time?.toLocaleString()}</td>
                      <td className="text-end">{iteration.input_tokens?.toLocaleString()}</td>
                      <td className="text-end">{iteration.output_tokens?.toLocaleString()}</td>
                      <td className="text-end">{iteration.total_tokens?.toLocaleString()}</td>
                      <td className="text-end">${iteration.input_token_cost?.toFixed(4)}</td>
                      <td className="text-end">${iteration.output_token_cost?.toFixed(4)}</td>
                      <td className="text-end">{iteration.remaining_allowance?.toLocaleString()}</td>
                    </tr>
                  ))
                }
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center p-3">
        <div className="text-muted small">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} entries
        </div>
        <Pagination>
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    </div>
  );
};

export default TokenUsageTable;